import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Device } from '@capacitor/device';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DynamoDBClient, ScanCommand, GetItemCommand } from '@aws-sdk/client-dynamodb';
import { AuthService } from '../auth/auth.service';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { environment } from 'src/environments/environment';
import { CognitoIdentityCredentials } from '@aws-sdk/credential-provider-cognito-identity';
import { CredentialsService, TwoNCredentials } from './credentials.service';
import { firstValueFrom } from 'rxjs';

export interface BluetoothCredentialSet {
  id: string;
  authId: string;
  keyId: string;
  locationId: string;
  publicKey: string;
}

export interface ResidentDataWithAllCredentials {
  resident: any;
  twoNCredentials: TwoNCredentials | null; // For SIP/video
  bluetoothCredentials?: BluetoothCredentialSet | null; // For BLE
}

@Injectable({
  providedIn: 'root'
})
export class DynamoDBService {
  private isPlatformCompatibleFor2N: boolean = false;
  private platformCheckComplete: boolean = false;

  // Cache for resident credentials to prevent multiple DynamoDB calls
  private cachedCredentialsResult: ResidentDataWithAllCredentials | null = null;
  private cachedCredentialsTimestamp: number = 0;
  private readonly CREDENTIALS_CACHE_TTL_MS = 30000; // 30 seconds
  private isCredentialsFetching = false;
  private credentialsFetchPromise: Promise<ResidentDataWithAllCredentials> | null = null;

  constructor(
    private authService: AuthService,
    private credentialsService: CredentialsService,
    private http: HttpClient,
    private platform: Platform
  ) {
    // console.log('DynamoDBService initialized');
    this.checkPlatformCompatibility();
  }

  /**
   * Check if platform is compatible for 2N features (physical iOS/Android devices only)
   */
  private async checkPlatformCompatibility(): Promise<void> {
    // First check if we're on Capacitor
    if (!this.platform.is('capacitor')) {
      this.isPlatformCompatibleFor2N = false;
      this.platformCheckComplete = true;
      return;
    }

    // Check if we're on iOS or Android
    if (!this.platform.is('ios') && !this.platform.is('android')) {
      this.isPlatformCompatibleFor2N = false;
      this.platformCheckComplete = true;
      return;
    }

    // Check if it's a real device (not simulator/emulator)
    try {
      const deviceInfo = await Device.getInfo();
      this.isPlatformCompatibleFor2N = !deviceInfo.isVirtual;
    } catch (error) {
      console.error('Error checking device info:', error);
      this.isPlatformCompatibleFor2N = false;
    }
    
    this.platformCheckComplete = true;
  }

  /**
   * Build a fresh DynamoDB client whose credentials provider re-fetches
   * the IdToken on every credential exchange. Construct on each call —
   * caching the client bakes in an IdToken that expires after an hour
   * and silently breaks every subsequent send() until app relaunch.
   */
  private buildClient(): DynamoDBClient {
    const loginsKey = `cognito-idp.${environment.awsRegion}.amazonaws.com/${environment.cognitoUserPoolId}`;
    const cognitoIdentityClient = new CognitoIdentityClient({
      region: environment.awsRegion
    });
    return new DynamoDBClient({
      region: environment.awsRegion,
      credentials: fromCognitoIdentityPool({
        client: cognitoIdentityClient,
        identityPoolId: environment.cognitoIdentityPoolId,
        logins: {
          [loginsKey]: () => this.authService.getValidIdToken()
        }
      })
    });
  }

  /**
   * Kept for backward compatibility with callers that invoke it on logout.
   * No-op now that the client is built per-call.
   */
  public clearClient(): void {
    this.clearCredentialsCache();
  }

  private async getCognitoUserId(): Promise<string | null> {
    try {
      return await this.authService.getValidCognitoUserId();
    } catch {
      return null;
    }
  }


  async verifyConnection() {
    try {
      // Verify we have a valid session first
      const session = this.authService.getSession();

      if (!session?.isValid()) {
        // This is expected during startup - use debug level
        console.debug('DynamoDB: Session not yet valid, deferring connection');
        return {
          success: false,
          message: 'No valid session found',
          error: new Error('No valid session found')
        };
      }

      const client = this.buildClient();

      const cognitoUserId = await this.getCognitoUserId();
      if (!cognitoUserId) {
        console.error('Could not get Cognito User ID');
        return {
          success: false,
          message: 'Could not get Cognito User ID',
          error: new Error('Could not get Cognito User ID')
        };
      }

      const scanCommand = new ScanCommand({
        TableName: environment.dynamoDBTableName,
        Limit: 1
      });

      const scanResult = await client.send(scanCommand);
      // console.log('Table structure:', scanResult.Items?.[0]);

      if (scanResult.Items && scanResult.Items.length > 0) {
        const firstItem = scanResult.Items[0];
        // console.log('Found item with keys:', Object.keys(firstItem));
        // console.log('First item id value:', firstItem['id']?.S);
        
        // Try to get the specific item using the Cognito User ID
        // console.log('Attempting to get item with Cognito User ID:', cognitoUserId);
        const getItemCommand = new GetItemCommand({
          TableName: environment.dynamoDBTableName,
          Key: {
            id: { S: cognitoUserId }
          }
        });

        const result = await client.send(getItemCommand);
        // console.log('GetItem result:', result.Item);
        if (!result.Item) {
          // console.log('No item found with ID:', cognitoUserId);
          // console.log('Available item IDs:', scanResult.Items.map(item => item['id']?.S));
        }

        return {
          success: true,
          message: 'Successfully connected to DynamoDB',
          hasTableAccess: true,
          tableStructure: Object.keys(firstItem),
          hasItem: !!result.Item,
          availableIds: scanResult.Items.map(item => item['id']?.S)
        };
      }

      return {
        success: true,
        message: 'Successfully connected to DynamoDB but no items found',
        hasTableAccess: true,
        hasItem: false
      };
    } catch (error) {
      console.error('Error verifying DynamoDB connection:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        error
      };
    }
  }

  async getResident(residentId: string) {
    // Ensure session is restored before initializing client
    await this.authService.ensureSessionRestored();

    const client = this.buildClient();

    const cognitoId = await this.getCognitoUserId();
    if (!cognitoId) {
      throw new Error('Could not get Cognito ID');
    }

    // Only allow access if the residentId matches the Cognito ID
    if (residentId !== cognitoId) {
      throw new Error('Unauthorized access to resident data');
    }

    const command = new GetItemCommand({
      TableName: environment.dynamoDBTableName,
      Key: {
        id: { S: residentId }
      }
    });

    try {
      const response = await client.send(command);
      return response.Item;
    } catch (error) {
      console.error('Error fetching resident:', error);
      throw error;
    }
  }

  /**
   * Get the current user's unit assignment using the lambda endpoint
   */
  async getUserUnit(): Promise<{ unitId: string | null }> {
    try {
      const cognitoId = await this.getCognitoUserId();
      if (!cognitoId) {
        console.error('Could not get Cognito ID for unit lookup');
        return { unitId: null };
      }

      console.log('[DynamoDB] Getting user unit from lambda endpoint');
      
      // Use the same lambda endpoint as getUserUnitInfo for consistency
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.airtableLambdaUrl}/user-unit`, {
          userId: cognitoId
        })
      );

      if (response.success && response.data && response.data.unitId) {
        console.log('[DynamoDB] User unit lookup result:', {
          hasUnit: true,
          unitId: response.data.unitId
        });
        return { unitId: response.data.unitId };
      } else {
        console.log('[DynamoDB] No unit found for user');
        return { unitId: null };
      }
    } catch (error: any) {
      // Suppress 403 errors in console as they're expected when API Gateway requires SigV4
      if (error?.status === 403) {
        console.debug('[DynamoDB] getUserUnit: 403 Forbidden (expected - API Gateway requires SigV4 authorization)');
      } else {
        console.error('[DynamoDB] Error fetching user unit:', error);
      }
      // Return null instead of throwing to allow graceful handling
      return { unitId: null };
    }
  }

  async getAllResidents() {
    await this.authService.ensureSessionRestored();

    const client = this.buildClient();

    const cognitoId = await this.getCognitoUserId();
    if (!cognitoId) {
      throw new Error('Could not get Cognito ID');
    }

    // Instead of scanning all residents, just get the one matching the Cognito ID
    const command = new GetItemCommand({
      TableName: environment.dynamoDBTableName,
      Key: {
        id: { S: cognitoId }
      }
    });

    try {
      const response = await client.send(command);
      return response.Item ? [response.Item] : [];
    } catch (error) {
      console.error('Error fetching resident:', error);
      throw error;
    }
  }

  /**
   * Retrieve resident data and extract 2N credentials if available
   */
  async getResidentWithCredentials(): Promise<{ resident: any, twoNCredentials: TwoNCredentials | null }> {
    const { resident, twoNCredentials } = await this.getResidentWithAllCredentials();
    return { resident, twoNCredentials };
  }

  /**
   * Extract 2N credentials from DynamoDB resident item
   */
  private extractTwoNCredentials(resident: any): TwoNCredentials | null {
    // Skip extraction on non-compatible platforms
    if (!this.isPlatformCompatibleFor2N) {
      // console.log('2N credentials extraction skipped - platform not compatible (web/simulator)');
      return null;
    }
    
    try {
      // console.log('Extracting 2N credentials from resident data...');
      
      // Look for the specific deviceId and devicePassword fields from DynamoDB
      let deviceId: string | undefined;
      let devicePassword: string | undefined;

      // Extract deviceId (stored as Number in DynamoDB)
      if (resident['deviceId']) {
        if (resident['deviceId'].N) {
          deviceId = resident['deviceId'].N;
          // console.log('Found deviceId (Number):', deviceId);
        } else if (resident['deviceId'].S) {
          deviceId = resident['deviceId'].S;
          // console.log('Found deviceId (String):', deviceId);
        } else if (typeof resident['deviceId'] === 'string') {
          deviceId = resident['deviceId'];
          // console.log('Found deviceId (direct):', deviceId);
        }
      }

      // Extract devicePassword (stored as String in DynamoDB)
      if (resident['devicePassword']) {
        if (resident['devicePassword'].S) {
          devicePassword = resident['devicePassword'].S;
          // console.log('Found devicePassword (String)');
        } else if (typeof resident['devicePassword'] === 'string') {
          devicePassword = resident['devicePassword'];
          // console.log('Found devicePassword (direct)');
        }
      }

      // Check if we have both required fields
      if (!deviceId || !devicePassword) {
        // console.log('Required 2N device credentials not found in resident data');
        // console.log('Available fields:', Object.keys(resident));
        // console.log('DeviceId found:', !!deviceId);
        // console.log('DevicePassword found:', !!devicePassword);
        return null;
      }

      // For 2N anonymous device login, we use:
      // - deviceId as the username
      // - devicePassword as the password
      // - type set to 'device' for anonymous device login
      const credentials: TwoNCredentials = {
        username: deviceId,           // deviceId becomes the username
        password: devicePassword,     // devicePassword becomes the password
        deviceId: deviceId,          // Also store as deviceId for reference
        url: 'https://my2n.com',     // Default 2N server URL
        type: 'device'               // Use device type for anonymous device login
      };

      // console.log('2N device credentials extracted successfully:', {
      //   username: credentials.username,
      //   hasPassword: !!credentials.password,
      //   deviceId: credentials.deviceId,
      //   url: credentials.url,
      //   type: credentials.type
      // });

      return credentials;
    } catch (error) {
      console.error('Error extracting 2N credentials:', error);
      return null;
    }
  }

  /**
   * Verify if the resident has 2N credentials and optionally refresh them
   */
  async verifyAndRefreshTwoNCredentials(): Promise<{ hasCredentials: boolean, refreshed: boolean }> {
    try {
      // console.log('Verifying and refreshing 2N credentials...');
      
      // Check if we already have stored credentials
      const hasStored = await this.credentialsService.hasTwoNCredentials();
      // console.log('Has stored credentials:', hasStored);

      // Always try to get fresh credentials from DynamoDB
      const { twoNCredentials } = await this.getResidentWithCredentials();
      
      if (twoNCredentials) {
        // console.log('Fresh 2N credentials retrieved from DynamoDB');
        return { hasCredentials: true, refreshed: true };
      } else if (hasStored) {
        // console.log('No fresh credentials in DynamoDB, but have stored credentials');
        return { hasCredentials: true, refreshed: false };
      } else {
        // console.log('No 2N credentials available');
        return { hasCredentials: false, refreshed: false };
      }
    } catch (error) {
      console.error('Error verifying and refreshing 2N credentials:', error);
      
      // Fallback to check stored credentials
      const hasStored = await this.credentialsService.hasTwoNCredentials();
      return { hasCredentials: hasStored, refreshed: false };
    }
  }

  /**
   * Get current credential status for debugging and testing
   */
  async getCredentialStatus(): Promise<{
    hasStoredCredentials: boolean;
    storedCredentials?: any;
    hasDynamoDBCredentials: boolean;
    dynamoDBCredentials?: any;
    residentData?: any;
  }> {
    try {
      // console.log('Getting comprehensive credential status...');
      
      // Check stored credentials
      const storedCredentials = await this.credentialsService.getTwoNCredentials();
      const hasStoredCredentials = !!storedCredentials;

      // Check DynamoDB credentials
      const { resident, twoNCredentials } = await this.getResidentWithCredentials();
      const hasDynamoDBCredentials = !!twoNCredentials;

      return {
        hasStoredCredentials,
        storedCredentials: storedCredentials ? {
          username: storedCredentials.username,
          hasPassword: !!storedCredentials.password,
          deviceId: storedCredentials.deviceId,
          url: storedCredentials.url,
          type: storedCredentials.type
        } : undefined,
        hasDynamoDBCredentials,
        dynamoDBCredentials: twoNCredentials ? {
          username: twoNCredentials.username,
          hasPassword: !!twoNCredentials.password,
          deviceId: twoNCredentials.deviceId,
          url: twoNCredentials.url,
          type: twoNCredentials.type
        } : undefined,
        residentData: resident ? Object.keys(resident) : undefined
      };
    } catch (error) {
      console.error('Error getting credential status:', error);
      return {
        hasStoredCredentials: false,
        hasDynamoDBCredentials: false
      };
    }
  }

  /**
   * Manual method to force refresh credentials from DynamoDB
   * Useful for testing and troubleshooting
   */
  async manualRefreshCredentials(): Promise<{ success: boolean, message: string, credentials?: any }> {
    try {
      // console.log('Manual credential refresh requested...');
      
      const { twoNCredentials } = await this.getResidentWithCredentials();
      
      if (twoNCredentials) {
        return {
          success: true,
          message: 'Credentials successfully retrieved and stored',
          credentials: {
            username: twoNCredentials.username,
            hasPassword: !!twoNCredentials.password,
            deviceId: twoNCredentials.deviceId,
            url: twoNCredentials.url,
            type: twoNCredentials.type
          }
        };
      } else {
        return {
          success: false,
          message: 'No 2N credentials found in resident data'
        };
      }
    } catch (error) {
      console.error('Error during manual credential refresh:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }


  /**
   * Extract 2N Bluetooth credentials from DynamoDB resident item
   */
  private extractBluetoothCredentials(resident: any): BluetoothCredentialSet | null {
    // Skip extraction on non-compatible platforms
    if (!this.isPlatformCompatibleFor2N) {
      return null;
    }

    if (resident && resident['bluetoothCredentials'] && resident['bluetoothCredentials'].M) {
      const bleCredsMap = resident['bluetoothCredentials'].M;

      // Extract locationId (can be Number or String in DynamoDB)
      let locationId: string | undefined;
      if (bleCredsMap['locationId']) {
        if (bleCredsMap['locationId'].N) {
          locationId = bleCredsMap['locationId'].N;
        } else if (bleCredsMap['locationId'].S) {
          locationId = bleCredsMap['locationId'].S;
        }
      }

      if (
        locationId &&
        bleCredsMap['keyId']?.S &&
        bleCredsMap['authId']?.S &&
        bleCredsMap['publicKey']?.S &&
        bleCredsMap['id']?.S
      ) {
        const credentials: BluetoothCredentialSet = {
          id: bleCredsMap['id'].S,
          locationId: locationId,
          keyId: bleCredsMap['keyId'].S,
          authId: bleCredsMap['authId'].S,
          publicKey: bleCredsMap['publicKey'].S,
        };
        console.debug('Bluetooth credentials extracted for location:', locationId);
        return credentials;
      } else {
        console.debug('Missing Bluetooth credential fields in resident data');
      }
    }
    return null;
  }

  /**
   * Retrieve resident data and extract all credentials (2N SIP and Bluetooth)
   * Results are cached for 30 seconds to prevent duplicate DynamoDB calls during startup
   */
  async getResidentWithAllCredentials(): Promise<ResidentDataWithAllCredentials> {
    // Check if we have a valid cached result
    const now = Date.now();
    if (this.cachedCredentialsResult && (now - this.cachedCredentialsTimestamp) < this.CREDENTIALS_CACHE_TTL_MS) {
      return this.cachedCredentialsResult;
    }

    // If already fetching, return the existing promise to prevent duplicate calls
    if (this.isCredentialsFetching && this.credentialsFetchPromise) {
      return this.credentialsFetchPromise;
    }

    this.isCredentialsFetching = true;
    this.credentialsFetchPromise = this.fetchResidentWithAllCredentials();

    try {
      const result = await this.credentialsFetchPromise;
      this.cachedCredentialsResult = result;
      this.cachedCredentialsTimestamp = Date.now();
      return result;
    } finally {
      this.isCredentialsFetching = false;
      this.credentialsFetchPromise = null;
    }
  }

  /**
   * Internal method to actually fetch credentials from DynamoDB
   */
  private async fetchResidentWithAllCredentials(): Promise<ResidentDataWithAllCredentials> {
    try {
      // console.log('Retrieving resident data with all credentials...');

      // Ensure session is restored before proceeding
      await this.authService.ensureSessionRestored();

      // Wait for platform check to complete
      if (!this.platformCheckComplete) {
        await this.checkPlatformCompatibility();
      }

      const cognitoId = await this.getCognitoUserId();
      if (!cognitoId) {
        throw new Error('Could not get Cognito ID');
      }

      const resident = await this.getResident(cognitoId);
      if (!resident) {
        // console.log('No resident data found');
        return { resident: null, twoNCredentials: null, bluetoothCredentials: null };
      }

      // console.log('Resident data retrieved, extracting credentials...');
      const twoNCredentials = this.extractTwoNCredentials(resident); // Existing SIP credentials
      const bluetoothCredentials = this.extractBluetoothCredentials(resident); // New BLE credentials

      // NOTE: Credential storage is handled by callers (e.g., two-n.service.ts via safeStoreTwoNCredentials)
      // to prevent duplicate storage operations. This function only retrieves/extracts credentials.

      return { resident, twoNCredentials, bluetoothCredentials };
    } catch (error) {
      console.error('Error retrieving resident with all credentials:', error);
      throw error;
    }
  }

  /**
   * Clear the credentials cache (call on logout)
   */
  public clearCredentialsCache(): void {
    this.cachedCredentialsResult = null;
    this.cachedCredentialsTimestamp = 0;
  }


  /**
   * Get unit information for the current user
   * Searches the Unit table for units where primaryTenantIds contains the user's ID
   */
  async getUserUnitInfo(): Promise<{
    buildingId?: string;
    buildingName?: string;
    buildingAddress?: string;
    floor?: string;
    unitName?: string;
    unitNumber?: string;
    bedrooms?: number;
    bathrooms?: number;
  } | null> {
    try {
      // Ensure session is restored before proceeding
      await this.authService.ensureSessionRestored();

      const cognitoUserId = await this.getCognitoUserId();
      if (!cognitoUserId) {
        console.log('No Cognito user ID available');
        return null;
      }

      let headers = new HttpHeaders();
      try {
        const authToken = await this.authService.getValidAccessToken();
        headers = headers.set('Authorization', authToken);
      } catch (e) {
        console.warn('[DynamoDB] No auth token available for /user-unit request', e);
      }

      // Call Lambda endpoint for secure unit lookup
      const response = await firstValueFrom(
        this.http.post<any>(
          `${environment.airtableLambdaUrl}/user-unit`,
          { userId: cognitoUserId },
          { headers }
        )
      );

      if (response.success && response.data) {
        // console.log('Unit info retrieved:', response.data);
        return response.data;
      } else {
        console.log('No unit found for user');
        return null;
      }

    } catch (error: any) {
      // Suppress 403 errors in console as they're expected when API Gateway requires SigV4
      // The app can function without this data - Property Manager info is loaded via GraphQL
      if (error?.status === 403) {
        console.debug('[DynamoDB] getUserUnitInfo: 403 Forbidden (expected - API Gateway requires SigV4 authorization)');
      } else {
        console.error('[DynamoDB] Error fetching user unit info:', error);
      }
      // Return null instead of throwing to allow graceful degradation
      return null;
    }
  }

  /**
   * Get Building information via Lambda endpoint (following the same pattern as getUserUnitInfo)
   * Returns property manager email and phone
   * Uses Lambda instead of direct DynamoDB access because IAM role doesn't have permissions
   * for Building table - Lambda has the necessary permissions
   */
  async getBuilding(buildingId: string): Promise<{
    propertyManagerEmail?: string;
    propertyManagerPhone?: string;
  } | null> {
    try {
      if (!buildingId) {
        console.log('[DynamoDB] No building ID provided');
        return null;
      }

      console.log(`[DynamoDB] ========== CALLING /building ENDPOINT ==========`);
      console.log(`[DynamoDB] Getting Building info via Lambda endpoint for buildingId: ${buildingId}`);
      console.log(`[DynamoDB] Endpoint URL: ${environment.airtableLambdaUrl}/building`);

      // Use Lambda endpoint for secure building lookup (same pattern as getUserUnitInfo)
      // Lambda has permissions to access Building table, while IAM role doesn't
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.airtableLambdaUrl}/building`, {
          buildingId: buildingId
        })
      );

      console.log('[DynamoDB] ========== RESPONSE FROM /building ==========');
      console.log('[DynamoDB] Response:', JSON.stringify(response, null, 2));
      console.log('[DynamoDB] Response.success:', response.success);
      
      if (response.data) {
        console.log('[DynamoDB] Response.data keys:', Object.keys(response.data));
        console.log('[DynamoDB] Response.data:', JSON.stringify(response.data, null, 2));
        console.log('[DynamoDB] Checking Property Manager fields in /building response:');
        console.log('  - propertyManagerEmail:', response.data.propertyManagerEmail);
        console.log('  - property_manager_email:', response.data.property_manager_email);
        console.log('  - propertyManager?.email:', response.data.propertyManager?.email);
        console.log('  - propertyManagerPhone:', response.data.propertyManagerPhone);
        console.log('  - property_manager_phone:', response.data.property_manager_phone);
        console.log('  - propertyManager?.phone:', response.data.propertyManager?.phone);
      }
      console.log('[DynamoDB] ==============================================');

      if (response.success && response.data) {

        const propertyManagerEmail = 
          response.data.propertyManagerEmail ||
          response.data.property_manager_email ||
          response.data.propertyManager?.email ||
          response.data.property_manager?.email ||
          response.data.managerEmail ||
          response.data.pmEmail;
          
        const propertyManagerPhone = 
          response.data.propertyManagerPhone ||
          response.data.property_manager_phone ||
          response.data.propertyManager?.phone ||
          response.data.property_manager?.phone ||
          response.data.managerPhone ||
          response.data.pmPhone;

        console.log('[DynamoDB] Extracted Property Manager data:', {
          hasEmail: !!propertyManagerEmail,
          hasPhone: !!propertyManagerPhone,
          email: propertyManagerEmail || 'NOT FOUND',
          phone: propertyManagerPhone || 'NOT FOUND'
        });

        if (propertyManagerEmail || propertyManagerPhone) {
          return {
            propertyManagerEmail: propertyManagerEmail,
            propertyManagerPhone: propertyManagerPhone
          };
        } else {
          console.warn('[DynamoDB] Building data found but no Property Manager fields detected');
          console.warn('[DynamoDB] Available fields:', Object.keys(response.data));
          return null;
        }
      } else {
        console.warn('[DynamoDB] No building found or Lambda returned error');
        console.warn('[DynamoDB] Response.success:', response.success);
        return null;
      }

    } catch (error: any) {
      console.error('[DynamoDB] ========== ERROR CALLING /building ==========');
      console.error('[DynamoDB] Error object:', error);
      console.error('[DynamoDB] Error status:', error.status);
      console.error('[DynamoDB] Error message:', error.message);
      console.error('[DynamoDB] Full error:', JSON.stringify(error, null, 2));
      
      // If endpoint doesn't exist (404), log helpful message
      if (error.status === 404 || error.status === 405) {
        console.warn('[DynamoDB] ========== ENDPOINT /building NOT FOUND ==========');
        console.warn('[DynamoDB] Lambda endpoint /building does not exist (404/405).');
        console.warn('[DynamoDB] This endpoint needs to be created in backend.');
        console.warn('[DynamoDB] Lambda should read from Building table and return propertyManagerEmail and propertyManagerPhone');
        console.warn('[DynamoDB] ===================================================');
      } else {
        console.error('[DynamoDB] Unexpected error fetching building via Lambda');
      }
      // Return null instead of throwing to allow graceful degradation
      return null;
    }
  }

  /**
   * Get complete user profile including resident data and unit info
   */
  async getUserProfile(): Promise<{
    name?: string;
    email?: string;
    phone?: string;
  }> {
    try {
      // Ensure session is restored before proceeding
      await this.authService.ensureSessionRestored();
      
      // Get resident data
      const { resident } = await this.getResidentWithAllCredentials();

      // Get Cognito attributes
      const cognitoAttributes = await new Promise<any>((resolve) => {
        const attributes: any = {};
        this.authService.getUserAttributes(
          (error: any) => {
            console.error('Error fetching Cognito attributes:', error);
            resolve(attributes); // Return empty attributes on error
          },
          (result: any) => {
            // Convert array of attributes to object
            if (result && Array.isArray(result)) {
              result.forEach((attr: any) => {
                attributes[attr.getName()] = attr.getValue();
              });
            }
            resolve(attributes);
          }
        );
      });

      // Combine all data (unit info now comes from GraphQL via user-profile-cache.service)
      const profile = {
        name: resident?.name?.S || `${cognitoAttributes?.['given_name'] || ''} ${cognitoAttributes?.['family_name'] || ''}`.trim(),
        email: cognitoAttributes?.['email'],
        phone: cognitoAttributes?.['phone_number']
      };

      // console.log('Complete user profile:', profile);
      return profile;

    } catch (error) {
      console.error('Error getting user profile:', error);
      // Return partial data that we can get
      try {
        const cognitoAttributes = this.authService.getUserAttributes();
        return {
          name: `${cognitoAttributes?.['given_name'] || ''} ${cognitoAttributes?.['family_name'] || ''}`.trim(),
          email: cognitoAttributes?.['email'],
          phone: cognitoAttributes?.['phone_number']
        };
      } catch {
        return {};
      }
    }
  }

} 